import classNames from "classnames";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { flatten } from "underscore";
import { CreateNewPasscodeList } from "../../../../../../../../connection/passcode-lists";
import { addRegistrationRequirement, removeRegistrationRequirement, setRegistrationType, updateRegistrationRequirement, } from "../../../../../../../../store/actions/admin/create-event";
import { addPasscodeList, getPasscodeLists } from "../../../../../../../../store/actions/admin/passcode-lists";
import { addChecklistItem } from "../../../../../../../../store/actions/authentication";
import { ChecklistTitles } from "../../../../../../../../store/reducers/admin/assistant";
import { EventsState } from "../../../../../../../../store/types";
import { GateTypes, PasscodeList, RegistrationTypes, Requirement, Session } from "../../../../../../../../types/working-model";
import { toDict } from "../../../../../../../../utils/utils";
import { showAlert } from "../../../../../../../general-ui/alert/alert-service";
import LargeButton from "../../../../../../../general-ui/button/large-button";
import Icon, { COLORS, ICONS } from "../../../../../../../general-ui/icon";
import ModalComponent from "../../../../../../../general-ui/modal/modal";
import { Popover } from "../../../../../../../general-ui/popover/popover";
import SelectInput from "../../../../../../../general-ui/select/select";
import SessionSelectInput from "../../../../../../../general-ui/select/session-select";
import Switch from "../../../../../../../general-ui/switch/switch";
import TagSelectInput from "../../../../../../../general-ui/tag-select/tag-select";
import TextInput, { Validation } from "../../../../../../../general-ui/text-input/text";
import { Tooltip } from "../../../../../../../general-ui/tooltip/tooltip";
import WaitingIndicator from "../../../../../../../general-ui/waiting-indicator/waiting-indicator";
import PasscodeListCreator, { WorkingPasscodeList } from "../../../../../registration/passcode-list";

const gateTypeOptions = [
	{
		label: "Domain email",
		value: GateTypes.domain_email
	},
	{
		label: "Shared passcode",
		value: GateTypes.shared_passcode
	},
	{
		label: "Passcode List",
		value: GateTypes.passcode_list
	}
];

function getEmptyGateRequirement(type = GateTypes.domain_email): Requirement {
	return {
		gatedSessions: [],
		type,
	};
}

const RegistrationGating: React.FC = () => {
	const {
		CreateEventReducer: {
			workingEvent
		},
		AuthReducer: {
			user,
			token
		},
		PasscodeListReducer: {
			passcodeLists
		},
	} = useSelector((state: EventsState) => state);
	const requirements = workingEvent?.registration_settings?.requirements;

	const isRegistrationGating = workingEvent?.registration_settings?.type === RegistrationTypes.gated;
	const emailGating = requirements?.[GateTypes.domain_email];
	const passcodeGating = requirements?.[GateTypes.shared_passcode];
	const passcodeListGatings = requirements?.[GateTypes.passcode_list];

	const [showRegistrationGating, setShowRegistrationGating] = useState(true);
	const [gatingModalOpen, setGatingModalOpen] = useState(false);
	// if user clicks cancel when gating modal first appears and no current gating requirements exits, turn gating off
	const [canCancelGating, setCanCancelGating] = useState(false);
	const [popoverOpen, setPopoverOpen] = useState<GateTypes | null>(null);
	const [openPasscodeListIndex, setOpenPasscodeListIndex] = useState<number | null>(null); //this will be the index of passcodeListGatings 
	const [gatingRequirementsOpen, setGatingRequirementsOpen] = useState(false);
	const [workingGateRequirement, setWorkingGateRequirement] = useState<Requirement | null>(null);
	const [loading, setLoading] = useState(false);
	const [passcodeListToCreate, setPasscodeListToCreate] = useState<WorkingPasscodeList>();
	const [formError, setFormError] = useState("");

	const dispatch = useDispatch();

	useEffect(() => {
		dispatch(getPasscodeLists(user!.active_channel, token!));
	}, [dispatch, token, user]);

	// passing a type in turns this into a Edit gate requierment function
	const addGatedRequirement = (type?: GateTypes) => {
		// determine if a requirment type does not exist, have that type loaded first, prioritize email
		const _type = !emailGating
			? GateTypes.domain_email
			: !passcodeListGatings && !passcodeLists.length
				? GateTypes.shared_passcode
				: GateTypes.passcode_list;
		setGateType(type || _type);
		setGatingRequirementsOpen(true);
	};
	const closeGatingRequirements = () => {
		if (canCancelGating) turnOffGating();
		setGatingRequirementsOpen(false);
		setOpenPasscodeListIndex(null);
	};

	const closePopover = () => {
		setPopoverOpen(null);
		if (!gatingRequirementsOpen) {
			setOpenPasscodeListIndex(null);
		}
	};
	const openPopover = (key: GateTypes, passcodeListIndex?: number) => {
		setOpenPasscodeListIndex(passcodeListIndex ?? null);
		setPopoverOpen(key);
	};

	const closeGatingModal = () => setGatingModalOpen(false);
	const turnOffGating = async () => {
		dispatch(setRegistrationType(RegistrationTypes.open));
		closeGatingModal();
	};
	const setIsRegistrationGating = (type: RegistrationTypes) => {
		if (type === RegistrationTypes.open) {
			setGatingModalOpen(true);
		} else {
			dispatch(setRegistrationType(type));
			addGatedRequirement();
			// if there are not any gating requirements, then canCancelGating
			requirements && Object.keys(requirements).length === 0 && setCanCancelGating(true);
		}
	};
	const removeRequirement = (type: GateTypes) => {
		// if deleting the last gating requirement, then turn off gating
		requirements && Object.keys(requirements)?.length === 1 && turnOffGating();
		dispatch(removeRegistrationRequirement(type, openPasscodeListIndex ?? undefined));
		setOpenPasscodeListIndex(null);
	};
	const codeOrListError = "Your event cannot have a shared passcode AND and passcode list.";
	const passcodeListNameError = "Your list must have a name.";
	const passcodeListURLError = "You must upload a passcode list.";
	const noSessionsError = "You must connect at least one Session to this list.";
	const formErrorMessage = () => {
		switch (workingGateRequirement?.type) {
			//only Passcode list OR shared pass code may exist at the same time
			case GateTypes.passcode_list: {
				if (passcodeGating) return codeOrListError;
				if (!workingGateRequirement.gatedSessions?.length) return noSessionsError;
				if (passcodeListToCreate && !passcodeListToCreate.url) return passcodeListURLError;
				if (passcodeListToCreate && !passcodeListToCreate.name) return passcodeListNameError;
				else return "";
			}

			case GateTypes.shared_passcode: {
				if (passcodeListGatings?.length) return codeOrListError;
				else if (!workingGateRequirement.sharedPasscode) return "Passcode field must have a value";
				else return "";
			}
			case GateTypes.domain_email: {
				if (!workingGateRequirement.allowedDomains?.length) return "Must include at least one email domain";
				//If the domain doesnt include either .** or .*** warn the user
				const domainRegex = /\.[a-z]{2,3}/i;
				for (const domain of workingGateRequirement.allowedDomains) {
					if (!domainRegex.test(domain)) {
						return "Please include an extension e.g. '.com'";
					}
				}
				return "";
			}
			default: return "";
		}
	};

	async function finished() {
		if (formErrorMessage()) {
			setFormError(formErrorMessage());
			return;
		} else {
			setFormError("");
		}
		setLoading(true);
		let _workingGateRequirement = workingGateRequirement;

		if (passcodeListToCreate && workingGateRequirement?.type === GateTypes.passcode_list) {
			const newPasscodeList = await CreateNewPasscodeList(token!, user!.active_channel, passcodeListToCreate);
			if (newPasscodeList.error) {
				showAlert({
					message: 'Unable to Create',
					description: 'Failed to create new passcode list',
					type: 'error',
					duration: 5000,
				});
				return setLoading(false);
			}

			dispatch(addPasscodeList(newPasscodeList));


			const previousList = workingGateRequirement?.passcodeList || [];
			_workingGateRequirement = workingGateRequirement
				? {
					...workingGateRequirement,
					passcodeList: [
						...previousList,
						(newPasscodeList.passcode_list)
					]
				}
				: null;
		}


		const type = _workingGateRequirement?.type;
		// To avoid duplicate requirements, check if they exist first then update them if the type doesnt exist then add it
		if (type === GateTypes.passcode_list && openPasscodeListIndex !== null) {
			//must pass the passcodeList index here
			dispatch(updateRegistrationRequirement(GateTypes.passcode_list, _workingGateRequirement!, openPasscodeListIndex));
		} else if (type === GateTypes.domain_email && emailGating || type === GateTypes.shared_passcode && passcodeGating) {
			dispatch(updateRegistrationRequirement(type, _workingGateRequirement!));
		} else {
			dispatch(addRegistrationRequirement(type!, _workingGateRequirement!));

			// ASSISTANT CHECKLIST ITEM: complete users checklist item for editing registration settings
			if (
				user &&
				!user.onboarded.includes(ChecklistTitles.editRegistration)
			) dispatch(addChecklistItem(token!, user.id, user.onboarded, ChecklistTitles.editRegistration));
		}

		setCanCancelGating(false);
		setOpenPasscodeListIndex(null);
		setWorkingGateRequirement(null);
		setGatingRequirementsOpen(false);
		setLoading(false);
		setPasscodeListToCreate(undefined);
	}
	function setSessionIds(sessions: number[]) {
		setWorkingGateRequirement((workingGateRequirement: Requirement | null) => {
			if (workingGateRequirement) {
				return ({ ...workingGateRequirement, gatedSessions: sessions });
			} else {
				return null;
			}
		});
	}
	function setGateType(value: GateTypes) {
		setFormError(""); //default to no form errors
		setWorkingGateRequirement(() => {
			// load the existing requirement if it exists, if not load an empty req
			switch (value) {
				case GateTypes.passcode_list: {
					if (passcodeGating) setFormError(codeOrListError); //display error message if they already have passcode gating
					//openPasscodeListIndex will be the index of the current passlist in passcodeListGatings
					return (passcodeListGatings?.length && openPasscodeListIndex !== null)
						? passcodeListGatings[openPasscodeListIndex]
						: getEmptyGateRequirement(GateTypes.passcode_list);
				}
				case GateTypes.shared_passcode: {
					if (passcodeListGatings?.length) setFormError(codeOrListError);//display error message if they already have passlist gating
					setPasscodeListToCreate(undefined);
					return passcodeGating ? passcodeGating : getEmptyGateRequirement(GateTypes.shared_passcode);
				}
				default: {
					setPasscodeListToCreate(undefined);
					return emailGating ? emailGating : getEmptyGateRequirement(GateTypes.domain_email);
				}
			}
		});

	}
	function setAllowedDomains(domains: string[]) {
		//remove @, and change to lower case
		const constrainedDomains = domains.map(domain => domain.replace("@", "").toLowerCase());

		setWorkingGateRequirement((workingGateRequirement: Requirement | null) => {
			if (workingGateRequirement) {
				return ({ ...workingGateRequirement, allowedDomains: constrainedDomains });
			} else {
				return null;
			}
		});
	}

	function setPasscode(e: React.ChangeEvent<HTMLInputElement>) {
		const val = e.target.value;
		setWorkingGateRequirement((workingGateRequirement: Requirement | null) => {
			if (workingGateRequirement) {
				return ({ ...workingGateRequirement, sharedPasscode: val });
			} else {
				return null;
			}
		});
	}

	function setWorkingPasscodeList(list: WorkingPasscodeList | undefined) {
		setPasscodeListToCreate(list);
	}
	function setPasscodeLists(lists: PasscodeList[]) {
		setWorkingGateRequirement((workingGateRequirement: Requirement | null) => {
			if (workingGateRequirement) {
				return ({ ...workingGateRequirement, passcodeList: lists.map((list: PasscodeList) => list.passcode_list!) });
			} else { return null; }
		});
	}
	function validGates(): Session[] {
		if (workingEvent?.registration_settings?.requirements) {
			//if there are passcode list requirements
			if (passcodeListGatings && passcodeListGatings?.length > 0) {

				//array of session ids that have passcode lists attached
				const sessionsWithLists = flatten(passcodeListGatings.map((requirement) => requirement.gatedSessions));

				//ensure that every session id is in the sessionsWithLists array
				const needsGates = workingEvent!.sessions.filter(session => !sessionsWithLists.includes(session.session));

				return needsGates;
			}
		}
		return [];
	}
	const invalidSessions = useMemo(validGates, [workingEvent, passcodeListGatings]);

	const passcodeListMap = useMemo(() => toDict('passcode_list', passcodeLists), [passcodeLists]);
	const renderPasslists = (currentPasscodeList: Requirement) => {
		const lists = currentPasscodeList
			?.passcodeList
			?.map((listId: number) => passcodeListMap[listId])
			?.filter(list => !!list)
			?? [];
		return (
			<ul className="tag-list">
				{lists.map((list: PasscodeList) => (
					<li className="tag" key={list?.passcode_list}>
						{list?.name}
					</li>
				))}
			</ul>
		);
	};

	const sessionMap = useMemo(() => toDict('session', workingEvent!.sessions), [workingEvent]);
	const renderGatedSessions = (currentPasscodeList: Requirement) => {
		const sessions = currentPasscodeList
			?.gatedSessions
			?.map((sessionId: number) => sessionMap[sessionId])
			?.filter(session => !!session)
			?? [];
		return (
			<ul className="tag-list">
				{sessions?.map((session: Session) => (
					<div className="tag" key={session.session}>
						{session.title?.base || ''}
					</div>
				))}
			</ul>
		);
	};

	const allowedDomains = useMemo(() => {
		return (<ul className="tag-list">
			{
				emailGating
					?.allowedDomains
					?.map((domain, i) => (
						<li className="tag capitalize" key={i}>
							{domain}
						</li>
					))
			}
		</ul>);
	}, [emailGating]);

	const getEditRequirementButton = (type: GateTypes, label: string, passcodeListIndex?: number) => {
		const isOpen = type !== GateTypes.passcode_list
			? popoverOpen === type
			: popoverOpen === type && passcodeListIndex === openPasscodeListIndex;
		return (
			<label>
				{label}
				<button onClick={() => openPopover(type, passcodeListIndex)} className="no-style options-button">
					<Icon name={ICONS.THREE_DOTS_VERTICAL} color={COLORS.WHITE} size={16} />
					<Popover
						open={isOpen}
						onClose={closePopover}
					>
						<button onClick={() => addGatedRequirement(type)}>Edit Requirements</button>
						<button onClick={() => removeRequirement(type)}>Delete</button>
					</Popover>
				</button>
			</label>
		);
	};

	const getTypeInfo = () => {
		switch (workingGateRequirement?.type) {
			case GateTypes.domain_email: {
				return "Allow registration for all emails associated with a domain listed in the email field.";
			}
			case GateTypes.passcode_list: {
				return "Allow registration for all users with a passcode below.";
			}
			case GateTypes.shared_passcode: {
				return "Allow registration for all users with a passcode below.";
			}
		}
	};


	const getTypeOptions = () => {
		switch (workingGateRequirement?.type) {
			case GateTypes.domain_email: {
				return (
					<div>
						<TagSelectInput
							onChange={setAllowedDomains}
							label={"Allowed Email Domains *"}
							initialTags={workingGateRequirement.allowedDomains ?? []}
							errorMessage={formError}
						/>
					</div>
				);
			}
			case GateTypes.passcode_list: {
				const workingList = (openPasscodeListIndex === null)
					? undefined
					: passcodeListGatings?.[openPasscodeListIndex]?.passcodeList?.map(id => passcodeListMap[id]);
				return (
					<PasscodeListCreator
						onChange={setPasscodeLists}
						onCreate={setWorkingPasscodeList}
						initialPasscodeLists={workingList}
						nameErrorMessage={formError === passcodeListNameError ? passcodeListNameError : undefined}
						urlErrorMessage={formError === passcodeListURLError ? passcodeListURLError : undefined}
					/>
				);
			}
			case GateTypes.shared_passcode: {
				return (
					<div>
						<TextInput
							placeholder={"Enter shared passcode"}
							label={"Shared Passcode"}
							onChange={setPasscode}
							defaultValue={workingGateRequirement?.sharedPasscode}
							valid={(formError !== codeOrListError) && formError ? Validation.error : Validation.normal}
							errorMessage={(formError !== codeOrListError) && formError || undefined}
						/>
					</div>
				);
			}
			default: return <></>;
		}

	};

	return (
		<>
			<div className="settings-card">
				<label>
					{isRegistrationGating && (
						<button className={classNames("no-style", { show: showRegistrationGating })} onClick={() => setShowRegistrationGating(prev => !prev)}>
							<Icon name={ICONS.KEYBOARD_ARROW_RIGHT} size={12} color={COLORS.WHITE} />
						</button>
					)}
					Registration gating
					<Tooltip tooltip="Grant access to your sessions based on individual session or tracks. Be sure to account for each session by assigning a unique registration type for each.">
						<Icon name={ICONS.PRIMARY_TOOLTIP} color={COLORS.CYAN} size={12} />
					</Tooltip>
				</label>
				<Switch
					value={"Single sign on"}
					on={isRegistrationGating}
					onClick={(_, isOn) => setIsRegistrationGating(isOn ? RegistrationTypes.gated : RegistrationTypes.open)}
				/>
			</div>
			{showRegistrationGating && isRegistrationGating && (
				<>
					{emailGating && (
						<div className="settings-card sub-list detailed-card">
							{getEditRequirementButton(GateTypes.domain_email, "Allowed email domains")}
							{allowedDomains}
						</div>
					)}
					{passcodeGating && (
						<div className="settings-card sub-list detailed-card">
							{getEditRequirementButton(GateTypes.shared_passcode, "Shared passcode")}
							<p className="tag clear">{passcodeGating?.sharedPasscode}</p>
						</div>
					)}
					{passcodeListGatings?.length
						? passcodeListGatings.map((passcodeList, index) => (
							<div key={index} className="settings-card sub-list detailed-card">
								{getEditRequirementButton(GateTypes.passcode_list, "Passcode lists", index)}
								{renderPasslists(passcodeList)}
								<p>Gated Sessions</p>
								{renderGatedSessions(passcodeList)}
							</div>
						))
						: null
					}

					<div className="left-margin">
						<LargeButton
							title={"Add Requirements"}
							//undefined needed for the exclusion logic to work.
							//otherwise itll send the click event and mess that up
							onClick={() => addGatedRequirement(undefined)}
						/>
					</div>
					{invalidSessions.length > 0 && (
						<div className="gate-errors left-margin">
							<p>If a passcode list is connected to any session, all sessions must include a passcode list.</p>
							<p>Please connect a passcode list to:</p>
							<ol>
								{invalidSessions.map((session: Session) => (
									<li key={session.uuid}>{session.title.base}</li>
								))}
							</ol>
						</div>
					)}
				</>
			)}

			{/* Confirmation Modal */}
			<ModalComponent
				size='small'
				closeable={false}
				cancellable={false}
				open={gatingModalOpen}
				onRequestClose={closeGatingModal}
				title={"Turn the registration gating off?"}
				footer={(
					<>
						<button onClick={closeGatingModal}>Cancel</button>
						<button onClick={turnOffGating} className="primary">
							Turn off gating
						</button>
					</>
				)}
			>
				<p>If you turn the registration gating off, all attendees will have the same type of access to the whole event content.</p>
			</ModalComponent>

			{/* GatingRequirements Settings Modal */}
			<ModalComponent
				size="medium"
				open={gatingRequirementsOpen}
				onRequestClose={closeGatingRequirements}
				title={"Gate Requirements"}
				cancellable={false}
				closeable={false}
				footer={(
					<>
						<button onClick={closeGatingRequirements}>Cancel</button>
						<button disabled={loading} onClick={finished} className="primary">{!loading ? "Apply" : <WaitingIndicator />}</button>
					</>
				)}
			>
				<div className="gating-requirements-modal">
					{workingGateRequirement?.type === GateTypes.passcode_list && (
						<SessionSelectInput
							sessions={workingEvent!.sessions}
							onChange={setSessionIds}
							placeholder={"Select at least one session"}
							preSelectedSessions={workingGateRequirement?.gatedSessions}
							errorMessage={formError === noSessionsError ? noSessionsError : undefined}
							ignoreInlineStyling
						/>
					)}

					<div className={classNames("gating-dropdown-container")}>
						<SelectInput
							options={gateTypeOptions}
							selected={workingGateRequirement?.type || GateTypes.domain_email}
							onChange={setGateType}
							label="Type of Gating*"
							info="TODO: type of gating tooltip text goes here"
							mainIcon={<Icon name={ICONS.KEYBOARD_ARROW_DOWN} size={12} color={`${COLORS.WHITE} select-icon`} />}
							errorMessage={formError === codeOrListError ? formError : undefined}
							ignoreSpacing
							ignoreInlineStyling
						/>
						<p className="info">{getTypeInfo()}</p>
					</div>
					{getTypeOptions()}
				</div>
			</ModalComponent>
		</>
	);
};

export default RegistrationGating;